import { useEffect, useId, useRef, type ReactNode } from 'react';
import { lockBodyScroll, setBackgroundInert } from 'pi-kiosk-shared/ui';
import { handleFocusTrapKeyDown } from '../shared/a11y/focusTrapWithin.js';
import {
  CUSTOMER_CHROME_PANEL_MAX_HEIGHT_CLASS,
  CUSTOMER_CHROME_PANEL_OVERFLOW_CLASS,
  CUSTOMER_CHROME_PANEL_RAIL_CLASS,
  CUSTOMER_CHROME_PANEL_WIDTH_CLASS,
  CUSTOMER_TOP_BAR_CLEARANCE_PT_CLASS,
} from './customerTopBarStyles.js';

export interface CustomerChromeTopPanelProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: ReactNode;
  readonly closeLabel: string;
  readonly testId: string;
  readonly titleId?: string;
  /** When set, maps to dialog `aria-describedby` (mirrors shared BottomSheet). */
  readonly describedBy?: string;
  readonly className?: string;
  readonly panelBusy?: boolean;
  /** Center title + body (empty cart). Default start keeps Profile left-aligned. */
  readonly contentAlign?: 'start' | 'center';
}

const CLOSE_BUTTON_CLASS = [
  'inline-flex h-11 min-h-11 w-11 min-w-11 shrink-0 items-center justify-center',
  'rounded-full border border-[var(--color-border)]',
  'text-[var(--color-on-surface-muted)]',
  'hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-on-surface)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

function CloseGlyph(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** Matches CLOSE_BUTTON_CLASS footprint — left spacer balances the close so centered titles share the panel midline with body content. */
const CLOSE_SLOT_CLASS = 'h-11 w-11 min-h-11 min-w-11 shrink-0';

/**
 * Top-anchored chrome panel under the fixed shell header (Profile / Settings / empty cart).
 * End-aligned on the same content rail as Org · Cart · Settings · Profile
 * (not viewport-centered). Prefs open from Settings sheet, not Profile.
 * Content-sized (`CUSTOMER_CHROME_PANEL_WIDTH_CLASS`) — never full shell width.
 * Backdrop click, Escape, and Tab focus trap (BottomSheet-peer) dismiss/contain.
 * Close stays in-flow; centered titles ignore the close for optical midline (equal side slots).
 *
 * Policy A (G13 / §7.4–§7.6 — CollectStepOverlay / BottomSheet peer):
 * `lockBodyScroll` + `setBackgroundInert(host)` while open. Shell header stays
 * VISIBLE above clearance but is inert/non-activatable — do not un-inert it.
 * Focus restores to the opener on close (§7.4).
 */
export function CustomerChromeTopPanel({
  open,
  onClose,
  title,
  children,
  closeLabel,
  testId,
  titleId: titleIdProp,
  describedBy,
  className,
  panelBusy = false,
  contentAlign = 'start',
}: CustomerChromeTopPanelProps): JSX.Element | null {
  const isCentered = contentAlign === 'center';
  const generatedId = useId();
  const titleId = titleIdProp ?? `${generatedId}-title`;
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const active = document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
    closeButtonRef.current?.focus();

    // Policy A: body scroll lock + background inert (siblings of host, not ancestors).
    const unlock = lockBodyScroll();
    const host = hostRef.current;
    const clearInert =
      host !== null ? setBackgroundInert(host) : (): void => undefined;

    // Tab trap + Escape — mirrors BottomSheet / CollectStepOverlay (capture phase).
    const onKeyDown = (event: KeyboardEvent): void => {
      const panel = panelRef.current;
      if (panel !== null) {
        handleFocusTrapKeyDown(panel, event);
      }
      if (event.key === 'Escape') {
        if (panelBusy) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearInert();
      unlock();
      const target = returnFocusRef.current;
      if (target !== null && document.contains(target)) {
        target.focus();
      }
    };
  }, [onClose, open, panelBusy]);

  if (!open) {
    return null;
  }

  return (
    <div
      ref={hostRef}
      className={[
        'customer-safe-area-x fixed inset-0 z-50 flex items-start',
        /* Clear fixed shell header (safe-area + 56px content row). */
        CUSTOMER_TOP_BAR_CLEARANCE_PT_CLASS,
      ].join(' ')}
      role="presentation"
      data-testid={`${testId}-backdrop`}
    >
      <button
        type="button"
        className={[
          'absolute inset-0',
          /* Softer scrim — enough to separate panel without burying the page. */
          'bg-black/30 dark:bg-black/40',
          'backdrop-blur-[1px] dark:backdrop-blur-[3px] motion-reduce:backdrop-blur-none',
        ].join(' ')}
        aria-label={closeLabel}
        tabIndex={-1}
        disabled={panelBusy}
        data-testid={`${testId}-overlay`}
        onClick={() => {
          if (!panelBusy) {
            onClose();
          }
        }}
      />
      {/* Same width rail as shell header so the panel sits under the end-icon cluster. */}
      <div className={['relative z-10 mt-1', CUSTOMER_CHROME_PANEL_RAIL_CLASS].join(' ')}>
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={describedBy || undefined}
          aria-busy={panelBusy || undefined}
          data-testid={testId}
          className={[
            CUSTOMER_CHROME_PANEL_MAX_HEIGHT_CLASS,
            CUSTOMER_CHROME_PANEL_OVERFLOW_CLASS,
            CUSTOMER_CHROME_PANEL_WIDTH_CLASS,
            'rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-lg',
            'sm:p-5',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <header
            className={
              isCentered
                ? /* Equal side slots + gap-x-2: title midline = panel/body midline; close clears title ink. */
                  'mb-3 grid grid-cols-[2.75rem_minmax(0,1fr)_2.75rem] items-center gap-x-2'
                : 'mb-3 flex items-start gap-3'
            }
          >
            {isCentered ? (
              <span className={CLOSE_SLOT_CLASS} aria-hidden="true" />
            ) : null}
            <h2
              id={titleId}
              className={[
                'm-0 min-w-0 truncate text-base font-semibold leading-tight text-[var(--color-on-surface)]',
                isCentered ? 'text-center' : 'flex-1 pt-1.5',
              ].join(' ')}
            >
              {title}
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              aria-label={closeLabel}
              className={[
                CLOSE_BUTTON_CLASS,
                isCentered ? 'justify-self-end' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={panelBusy}
              data-testid={`${testId}-close`}
              onClick={onClose}
            >
              <CloseGlyph />
            </button>
          </header>
          {children}
        </div>
      </div>
    </div>
  );
}
